import { cn } from '@/lib/utils';

interface CapacityBarProps {
  registered: number;
  total: number;
  className?: string;
}

/**
 * Two-segment capacity bar.
 *
 * Under/at capacity: single primary-color fill, scaled 0..100% of the bar.
 *
 * Over capacity: bar represents the full registered count. The capacity
 * portion fills in primary color up to `(total / registered)` of the bar;
 * the overflow fills the remainder in destructive color. This makes the
 * ratio of "real spots vs over-capacity entries" visually obvious — e.g.
 * 15 entries on a max of 12 shows 12 worth of primary fill (80% of bar)
 * and 3 worth of overflow (20% of bar) instead of glitching past 100%.
 */
export function CapacityBar({ registered, total, className }: CapacityBarProps) {
  if (total <= 0) return null;

  const isOver = registered > total;

  if (!isOver) {
    const pct = Math.round((registered / total) * 100);
    return (
      <div
        className={cn(
          'relative h-2 w-full overflow-hidden rounded-full bg-primary/20',
          className,
        )}
      >
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  }

  const capacityWidth = (total / registered) * 100;
  const overflowWidth = 100 - capacityWidth;

  return (
    <div
      className={cn(
        'relative flex h-2 w-full overflow-hidden rounded-full bg-primary/20',
        className,
      )}
    >
      <div
        className="h-full bg-emerald-500"
        style={{ width: `${capacityWidth}%` }}
      />
      <div
        className="h-full bg-destructive"
        style={{ width: `${overflowWidth}%` }}
      />
    </div>
  );
}
